import { ChainId, ChainType } from '@lifi/sdk'
import { SuiProvider as SuiSDKProvider } from '@lifi/sdk-provider-sui'
import { SuiContext } from '@lifi/widget-provider'
import {
  useConnectWallet,
  useCurrentWallet,
  useDisconnectWallet,
  useWallets,
} from '@mysten/dapp-kit'
import type { WalletWithRequiredFeatures } from '@mysten/wallet-standard'
import { type FC, type PropsWithChildren, useCallback, useMemo } from 'react'

interface SuiProviderValuesProps {
  isExternalContext: boolean
}

export const SuiProviderValues: FC<
  PropsWithChildren<SuiProviderValuesProps>
> = ({ children, isExternalContext }) => {
  const wallets = useWallets()
  const { currentWallet, connectionStatus } = useCurrentWallet()
  const { mutateAsync: disconnect } = useDisconnectWallet()
  const { mutateAsync: connect } = useConnectWallet()

  const account =
    currentWallet?.accounts?.length && connectionStatus === 'connected'
      ? {
          address: currentWallet?.accounts[0].address,
          chainId: ChainId.SUI,
          chainType: ChainType.MVM,
          connector: currentWallet,
          isConnected: connectionStatus === 'connected',
          isConnecting: false,
          isReconnecting: false,
          isDisconnected: !currentWallet,
          status: connectionStatus,
        }
      : {
          chainType: ChainType.MVM,
          isConnected: false,
          isConnecting: false,
          isReconnecting: false,
          isDisconnected: true,
          status: 'disconnected' as const,
        }

  const installedWallets = wallets

  const isConnected = account.isConnected

  const sdkProvider = useMemo(
    () =>
      SuiSDKProvider({
        getWallet: async () => currentWallet!,
      }),
    [currentWallet]
  )

  const handleConnect = useCallback(
    async (
      connectorIdOrName: string,
      onSuccess?: (address: string, chainId: number) => void
    ) => {
      const connector = wallets.find(
        (wallet) => (wallet.id ?? wallet.name) === connectorIdOrName
      )
      if (connector) {
        await connect(
          { wallet: connector as WalletWithRequiredFeatures },
          {
            onSuccess: (standardConnectOutput) => {
              onSuccess?.(
                standardConnectOutput.accounts[0].address,
                ChainId.SUI
              )
            },
          }
        )
      }
    },
    [connect, wallets]
  )

  return (
    <SuiContext.Provider
      value={{
        isEnabled: true,
        account,
        sdkProvider,
        installedWallets,
        isConnected,
        isExternalContext,
        connect: handleConnect,
        disconnect,
      }}
    >
      {children}
    </SuiContext.Provider>
  )
}
